import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import Card from './shared/Card';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import errorKbService from '../services/errorKbService';
import syncTemplatesService from '../services/syncTemplatesService';
import errorSignature from '../utils/errorSignature';
import './KnowledgeBaseAdmin.css';

// Superadmin management for the Error Knowledge Base. Entries explain what an error means and how to
// fix it, scoped to a sync template and matched to error lines by a normalised signature.
export default function KnowledgeBaseAdmin() {
    const [entries, setEntries] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filterTemplate, setFilterTemplate] = useState('');
    const [search, setSearch] = useState('');

    const [editor, setEditor] = useState(null);   // { id?, syncTemplateId, title, description, sampleMessage }
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        setLoading(true); setError(null);
        try {
            const [kb, tpl] = await Promise.all([
                errorKbService.adminList(),
                syncTemplatesService.listTemplates().catch(() => ({})),
            ]);
            setEntries(kb.entries || []);
            const list = tpl.templates || tpl.data || tpl || [];
            setTemplates(Array.isArray(list) ? list : []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load Knowledge Base');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const templateName = (id) => {
        const t = templates.find(x => String(x.synctemplateid ?? x.syncTemplateId ?? x.id) === String(id));
        return t ? (t.templatename || t.templateName || t.name) : `Template ${id}`;
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return entries.filter(e => {
            if (filterTemplate && String(e.syncTemplateId) !== String(filterTemplate)) return false;
            if (!q) return true;
            return [e.title, e.description, e.sampleMessage, e.errorSignature]
                .some(v => (v || '').toLowerCase().includes(q));
        });
    }, [entries, filterTemplate, search]);

    const openAdd = () => { setSaveError(null); setEditor({ syncTemplateId: filterTemplate || '', title: '', description: '', sampleMessage: '' }); };
    const openEdit = (e) => { setSaveError(null); setEditor({ id: e.id, syncTemplateId: e.syncTemplateId, title: e.title, description: e.description, sampleMessage: e.sampleMessage || '' }); };

    const handleSave = async () => {
        const ed = editor;
        if (!ed.syncTemplateId) { setSaveError('Select a sync template'); return; }
        if (!ed.title.trim() || !ed.description.trim()) { setSaveError('Title and description are required'); return; }
        const sig = errorSignature(ed.sampleMessage);
        if (!sig) { setSaveError('Enter a sample error message so the entry can be matched'); return; }
        setSaving(true); setSaveError(null);
        try {
            if (ed.id) {
                await errorKbService.update(ed.id, {
                    title: ed.title.trim(), description: ed.description.trim(),
                    errorSignature: sig, sampleMessage: ed.sampleMessage,
                });
            } else {
                await errorKbService.create({
                    syncTemplateId: parseInt(ed.syncTemplateId, 10), errorSignature: sig,
                    title: ed.title.trim(), description: ed.description.trim(), sampleMessage: ed.sampleMessage,
                });
            }
            setEditor(null);
            await load();
        } catch (e) {
            setSaveError(e.response?.data?.message || 'Failed to save entry');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try { await errorKbService.remove(deleteTarget.id); setDeleteTarget(null); await load(); }
        catch (e) { setError(e.response?.data?.message || 'Failed to delete entry'); setDeleteTarget(null); }
    };

    return (
        <div className="kb-admin">
            <div className="kb-admin-header">
                <div>
                    <h1>Knowledge Base</h1>
                    <p className="kb-admin-sub">Explain what sync errors mean and how to fix them. Knowledge is scoped to a sync template and shown to users on matching error lines.</p>
                </div>
                <Button onClick={openAdd}><Plus size={16} /> Add Entry</Button>
            </div>

            <Card>
                <div className="kb-admin-filters">
                    <select value={filterTemplate} onChange={e => setFilterTemplate(e.target.value)}>
                        <option value="">All templates</option>
                        {templates.map(t => {
                            const id = t.synctemplateid ?? t.syncTemplateId ?? t.id;
                            return <option key={id} value={id}>{t.templatename || t.templateName || t.name}</option>;
                        })}
                    </select>
                    <div className="kb-admin-search">
                        <Search size={15} />
                        <input type="text" placeholder="Search title, error, description…" value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                </div>

                {loading ? (
                    <div className="kb-admin-loading"><Spinner size="md" /></div>
                ) : error ? (
                    <div className="kb-admin-error">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="kb-admin-empty">No Knowledge Base entries{filterTemplate || search ? ' match your filters' : ' yet'}.</div>
                ) : (
                    <div className="kb-admin-table-wrap">
                        <table className="kb-admin-table">
                            <thead>
                                <tr>
                                    <th>Template</th>
                                    <th>Title</th>
                                    <th>Matched error</th>
                                    <th>Fix</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(e => (
                                    <tr key={e.id}>
                                        <td>{e.templateName || templateName(e.syncTemplateId)}</td>
                                        <td className="kb-admin-title-cell">{e.title}</td>
                                        <td><code className="kb-admin-sig" title={e.errorSignature}>{e.sampleMessage || e.errorSignature}</code></td>
                                        <td className="kb-admin-desc-cell">{e.description}</td>
                                        <td className="kb-admin-actions">
                                            <button className="icon-button" title="Edit" onClick={() => openEdit(e)}><Edit size={16} /></button>
                                            <button className="icon-button danger" title="Delete" onClick={() => setDeleteTarget(e)}><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {editor && (
                <div className="modal-overlay" onClick={() => !saving && setEditor(null)}>
                    <div className="modal-content modal-md" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editor.id ? 'Edit Knowledge Base Entry' : 'Add Knowledge Base Entry'}</h2>
                            <button className="modal-close" onClick={() => setEditor(null)}>×</button>
                        </div>
                        <div className="modal-body kb-editor-body">
                            <label className="kb-field">
                                <span>Sync template <span className="kb-req">*</span></span>
                                <select value={editor.syncTemplateId} disabled={!!editor.id}
                                    onChange={e => setEditor({ ...editor, syncTemplateId: e.target.value })}>
                                    <option value="">Select a template…</option>
                                    {templates.map(t => {
                                        const id = t.synctemplateid ?? t.syncTemplateId ?? t.id;
                                        return <option key={id} value={id}>{t.templatename || t.templateName || t.name}</option>;
                                    })}
                                </select>
                            </label>
                            <label className="kb-field">
                                <span>Sample error message <span className="kb-req">*</span></span>
                                <textarea rows={3} value={editor.sampleMessage}
                                    placeholder="Paste an example of the error message"
                                    onChange={e => setEditor({ ...editor, sampleMessage: e.target.value })} />
                                {editor.sampleMessage && (
                                    <span className="kb-sig-preview">Match signature: <code>{errorSignature(editor.sampleMessage)}</code></span>
                                )}
                            </label>
                            <label className="kb-field">
                                <span>Title <span className="kb-req">*</span></span>
                                <input type="text" maxLength={255} value={editor.title}
                                    onChange={e => setEditor({ ...editor, title: e.target.value })} />
                            </label>
                            <label className="kb-field">
                                <span>What it means &amp; how to fix it <span className="kb-req">*</span></span>
                                <textarea rows={6} value={editor.description}
                                    onChange={e => setEditor({ ...editor, description: e.target.value })} />
                            </label>
                            {saveError && <div className="kb-admin-error">{saveError}</div>}
                        </div>
                        <div className="modal-footer">
                            <Button variant="secondary" onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Knowledge Base entry?"
                    message={`"${deleteTarget.title}" will be removed. This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}
